import React from 'react';
import { useTranslation } from 'react-i18next';


interface StatsCalendarProps {
  year: number;
  month: number;
  months: string[];
  onDateSelect: (date: string) => void;
  onMonthChange: (year: number, month: number) => void;
}

export const StatsCalendar: React.FC<StatsCalendarProps> = ({
  year,
  month,
  months,
  onDateSelect,
  onMonthChange
}) => {
  const { t, i18n } = useTranslation();

  // Русские дни недели
  const ruWeekdays = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
  
  // Английские дни недели
  const enWeekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const weekdays = i18n.language === 'ENG' ? enWeekdays : ruWeekdays;

  // Получаем календарь для месяца
  const getCalendarDays = (year: number, month: number) => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const today = new Date();
    
    const days = [];
    
    // Получаем день недели для первого дня месяца (0 = воскресенье, 1 = понедельник, ...)
    const firstDayOfWeek = firstDay.getDay();
    
    // Вычисляем, сколько пустых ячеек нужно в начале (для понедельника = 0, вторника = 1, ...)
    const emptyCellsAtStart = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    
    // Добавляем пустые ячейки в начале
    for (let i = 0; i < emptyCellsAtStart; i++) {
      days.push({
        date: null,
        day: null,
        isCurrentMonth: false,
        isToday: false,
        isFuture: false,
        isBefore2025: false,
        isEmpty: true
      });
    }
    
    // Добавляем дни текущего месяца
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const currentDate = new Date(year, month - 1, day);
      const isToday = currentDate.toDateString() === today.toDateString();
      const isFuture = currentDate > today;
      const isBefore2025 = currentDate < new Date(2025, 0, 1);
      
      days.push({
        date: currentDate,
        day: day,
        isCurrentMonth: true,
        isToday,
        isFuture,
        isBefore2025,
        isEmpty: false
      });
    }
    
    // Добавляем пустые ячейки в конце, чтобы заполнить сетку 6x7
    const totalCells = 42; // 6 недель * 7 дней
    const remainingCells = totalCells - days.length;
    
    for (let i = 0; i < remainingCells; i++) {
      days.push({
        date: null,
        day: null,
        isCurrentMonth: false,
        isToday: false,
        isFuture: false,
        isBefore2025: false,
        isEmpty: true
      });
    }
    
    return days;
  };

  const calendarDays = getCalendarDays(year, month);

  const handleDateClick = (day: { date: Date | null; isCurrentMonth: boolean; isFuture: boolean; isBefore2025: boolean; isEmpty: boolean }) => {
    if (day.isEmpty || !day.isCurrentMonth || day.isFuture || day.isBefore2025 || !day.date) return;
    
    const dateString = day.date.toLocaleDateString('en-CA'); // YYYY-MM-DD в локальном времени
    onDateSelect(dateString);
  };

  const handlePrevMonth = () => {
    // Ограничиваем навигацию до января 2025 года
    if (month === 1) {
      if (year > 2025) {
        onMonthChange(year - 1, 12);
      }
    } else {
      onMonthChange(year, month - 1);
    }
  };

  const handleNextMonth = () => {
    if (month === 12) {
      onMonthChange(year + 1, 1);
    } else {
      onMonthChange(year, month + 1);
    }
  };

  return (
    <div className="space-y-3">
      {/* Дни недели */}
      <div className="grid grid-cols-7 gap-x-9 md:gap-x-11 gap-y-1 px-4">
        {weekdays.map((day, index) => (
          <div
            key={index}
            className="text-center text-xs font-medium text-white py-2 w-8 h-8 flex items-center justify-center"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Календарная сетка */}
      <div className="grid grid-cols-7 gap-x-9 md:gap-x-11 gap-y-1 px-4">
        {calendarDays.map((day, index) => (
          day.isEmpty ? (
            <div key={index} className="w-8 h-8"></div>
          ) : (
            <button
              key={index}
              onClick={() => handleDateClick(day)}
              disabled={!day.isCurrentMonth || day.isFuture || day.isBefore2025}
              className={`
                w-8 h-8 text-sm rounded-md transition-colors border border-border focus:outline-none focus:ring-0
                ${day.isCurrentMonth 
                  ? day.isToday
                    ? 'bg-primary text-primary-foreground font-bold border-primary' // Сегодня
                    : day.isFuture
                      ? 'text-textSecondary cursor-not-allowed bg-header' // Будущее
                                          : day.isBefore2025
                      ? 'text-textSecondary cursor-not-allowed bg-header' // До 2025 года
                      : 'text-white cursor-pointer bg-header' // Обычный день
                  : 'text-textSecondary cursor-not-allowed bg-header' // Другой месяц
                }
              `}
            >
              {day.isToday ? `[${day.day}]` : day.day}
            </button>
          )
        ))}
      </div>

      {/* Навигация по месяцам */}
      <div className="grid grid-cols-7 gap-x-9 md:gap-x-11 gap-y-1 pt-2 px-4">
        <button
          onClick={handlePrevMonth}
          className="w-8 h-8 text-sm rounded-md transition-colors border border-border text-textSecondary focus:outline-none focus:ring-0 flex items-center justify-center"
        >
          &lt;
        </button>
        
        <div className="col-span-5 flex items-center justify-center">
          <div className="text-sm text-white font-semibold">
            {months[month]} {year}
          </div>
        </div>
        
        <button
          onClick={handleNextMonth}
          className="w-8 h-8 text-sm rounded-md transition-colors border border-border text-textSecondary focus:outline-none focus:ring-0 flex items-center justify-center"
        >
          &gt;
        </button>
      </div>
    </div>
  );
}; 